import { HttpErrorResponse } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@price-management-console/auth';
import { NotificationService } from '@price-management-console/shared-ui';
import { ColumnState } from 'ag-grid-community';
import { EMPTY, pipe } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { mapApiToColumnState, mapColumnStateToApi } from './grid-state.mapper';
import {
  GRID_PREFERENCE_CONFIG,
  GridConfig,
  GridId,
} from './preference-api.types';
import { UserPreferenceApiService } from './user-preference-api.service';

export interface GridPreferencesState {
  preferences: Partial<Record<GridId, ColumnState[]>>;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

const initialState: GridPreferencesState = {
  preferences: {},
  isLoading: false,
  isSaving: false,
  error: null,
};

export const GridPreferencesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods(
    (
      store,
      api = inject(UserPreferenceApiService),
      auth = inject(AuthService),
      injector = inject(Injector),
      notificationService = inject(NotificationService),
      translate = inject(TranslateService)
    ) => {
      // Internal helper to convert store.preferences() map into backend-formatted GridConfig[]
      const getGridConfigsFromState = (): GridConfig[] => {
        return Object.entries(store.preferences()).map(([gridName, states]) =>
          mapColumnStateToApi(gridName, states)
        );
      };

      // Stand-alone RxJS sub-stream to fetch and map grid preferences from API
      const fetchPreferencesStream = (userId: string) => {
        return api
          .getPreferences(
            userId,
            GRID_PREFERENCE_CONFIG.APPLICATION_ID,
            GRID_PREFERENCE_CONFIG.CATEGORY,
            GRID_PREFERENCE_CONFIG.SUBCATEGORY,
            GRID_PREFERENCE_CONFIG.PREFERENCE_CODE
          )
          .pipe(
            tap((response) => {
              const mappedPreferences = {} as Record<GridId, ColumnState[]>;
              if (response?.preferenceValue?.data) {
                response.preferenceValue.data.forEach((gridConfig) => {
                  mappedPreferences[gridConfig.gridName as GridId] =
                    mapApiToColumnState(gridConfig);
                });
              }
              patchState(store, {
                preferences: mappedPreferences,
                isLoading: false,
              });
            }),
            catchError((err: HttpErrorResponse) => {
              console.warn(
                'Failed to load preferences. Loading empty preferences.',
                err
              );
              patchState(store, {
                preferences: {},
                error: null,
                isLoading: false,
              });
              return EMPTY;
            })
          );
      };

      return {
        /**
         * Loads grid preferences for the given preference code from the backend API, maps them, and updates local state.
         */
        loadPreferences: rxMethod<void>(
          pipe(
            tap(() => {
              patchState(store, {
                isLoading: true,
                error: null,
              });
            }),
            switchMap(() => {
              // Reactively wait for the auth.userId signal to emit a non-null string using the provided injection context
              return toObservable(auth.userId, { injector }).pipe(
                filter((userId): userId is string => !!userId),
                take(1),
                switchMap((currentUserId) =>
                  fetchPreferencesStream(currentUserId)
                )
              );
            })
          )
        ),

        /**
         * Persists the current in-memory grid preferences to the backend database
         * for the specific grid identified by the preferenceCode.
         * Integrates local state updates before persisting to API.
         */
        saveGridPreference: rxMethod<{ gridId: GridId; states: ColumnState[] }>(
          pipe(
            tap(({ gridId, states }) => {
              const currentPrefs = store.preferences();
              patchState(store, {
                preferences: { ...currentPrefs, [gridId]: states },
                isSaving: true,
                error: null,
              });
            }),
            switchMap(() => {
              const currentUserId = auth.userId();
              if (!currentUserId) {
                patchState(store, {
                  error: 'User is not authenticated',
                  isSaving: false,
                });
                return EMPTY;
              }

              const allGridConfigs = getGridConfigsFromState();
              return api
                .savePreferences(
                  currentUserId,
                  allGridConfigs,
                  GRID_PREFERENCE_CONFIG.APPLICATION_ID,
                  GRID_PREFERENCE_CONFIG.CATEGORY,
                  GRID_PREFERENCE_CONFIG.SUBCATEGORY,
                  GRID_PREFERENCE_CONFIG.PREFERENCE_CODE
                )
                .pipe(
                  tap(() => {
                    patchState(store, { isSaving: false });
                    notificationService.showSuccess(
                      translate.instant('GRID_PREFERENCES.SAVED_SUCCESSFULLY')
                    );
                  }),
                  catchError((err: HttpErrorResponse) => {
                    const errorMsg =
                      err.error?.message ||
                      err.message ||
                      translate.instant('GRID_PREFERENCES.SAVE_FAILED');
                    notificationService.showError(errorMsg);
                    patchState(store, {
                      error: errorMsg,
                      isSaving: false,
                    });
                    return EMPTY;
                  })
                );
            })
          )
        ),
      };
    }
  )
);
